import { z } from 'zod';
import { Currency } from '../enums.js';
import { IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

/**
 * S16-BE.1 — Budget (плановые показатели по периоду / cost-center / project).
 * Budget line = план по account_code. Format: `NNNN-[a-z_]+`.
 */

export const BudgetState = z.enum(['draft', 'pending_approval', 'active', 'locked', 'archived']);
export type BudgetState = z.infer<typeof BudgetState>;

export const BudgetAccountCode = z
  .string()
  .regex(/^\d{4}-[a-z_]+$/, 'account_code_must_match_pattern_NNNN-[a-z_]+');
export type BudgetAccountCode = z.infer<typeof BudgetAccountCode>;

export const BudgetLineDirection = z.enum(['revenue', 'cost']);
export type BudgetLineDirection = z.infer<typeof BudgetLineDirection>;

export const BudgetPeriodSplit = z.enum(['quarterly', 'monthly', 'lump_sum']);
export type BudgetPeriodSplit = z.infer<typeof BudgetPeriodSplit>;

export const BudgetLine = z.object({
  line_no: z.number().int().min(1),
  account_code: BudgetAccountCode,
  account_name_ru: z.string(),
  direction: BudgetLineDirection,
  plan_amount: MoneyAmount,
  period_split: BudgetPeriodSplit,
  notes_ru: z.string().nullable(),
});
export type BudgetLine = z.infer<typeof BudgetLine>;

export const Budget = z.object({
  id: UuidString,
  entity_id: UuidString,
  state: BudgetState,
  name_ru: z.string().min(1).max(255),
  project_tag: z.string().nullable(),
  period_from: IsoDate,
  period_to: IsoDate,
  currency: Currency,
  lines: z.array(BudgetLine).min(1),
  total_revenue_plan: MoneyAmount,
  total_cost_plan: MoneyAmount,
  total_profit_plan: MoneyAmount,
  created_at: IsoDateTime,
  // EFE-13/14: updated_at для optimistic concurrency + detail page footer.
  updated_at: IsoDateTime.optional(),
  activated_at: IsoDateTime.nullable(),
  locked_at: IsoDateTime.nullable(),
  created_by: UuidString,
  activated_by: UuidString.nullable(),
});
export type Budget = z.infer<typeof Budget>;
